// ============================================
// PAGE - Ecommerce
// ============================================
// Gestión de pedidos del ecommerce

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MdSearch, MdVisibility, MdQrCode2, MdRefresh, MdOpenInNew, MdContentCopy } from 'react-icons/md';
import { usePedidos, useSucursalActual, useCancelarPedido } from '@almacenes/core';
import type { Pedido, PedidosFiltros } from '@almacenes/core';
import { useUIStore } from '@almacenes/core';
import { ModalDetallePedido } from './components/ModalDetallePedido';

export const EcommercePage = () => {
  const [searchParams] = useSearchParams();
  const [filtros, setFiltros] = useState<PedidosFiltros>({});
  const [inputCliente, setInputCliente] = useState('');
  const [inputEstado, setInputEstado] = useState('');
  const [inputFechaDesde, setInputFechaDesde] = useState('');
  const [inputFechaHasta, setInputFechaHasta] = useState('');
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState<Pedido | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Leer filtros de la URL al cargar el componente
  useEffect(() => {
    const estadoParam = searchParams.get('estado');
    if (estadoParam && ['PENDIENTE', 'CANCELADO', 'COMPLETADO'].includes(estadoParam)) {
      setInputEstado(estadoParam);
      setFiltros({ estado: estadoParam as 'PENDIENTE' | 'CANCELADO' | 'COMPLETADO' });
      setShouldFetch(true); // Activar búsqueda si viene desde URL con filtro
    }
  }, [searchParams]);

  const { data, isLoading, error, refetch } = usePedidos(filtros, shouldFetch);
  const { data: sucursalActual } = useSucursalActual();
  const { mutateAsync: cancelarPedido } = useCancelarPedido();
  const addToast = useUIStore((state) => state.addToast);

  // Construir URL del ecommerce
  const almacenId = sucursalActual?.id;
  const ecommerceUrl = almacenId ? `${window.location.origin}/tienda/${almacenId}` : '';
  const qrCodeUrl = almacenId ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(ecommerceUrl)}` : '';

  const handleBuscar = () => {
    setFiltros({
      cliente: inputCliente.length >= 3 ? inputCliente : undefined,
      estado: inputEstado ? (inputEstado as 'PENDIENTE' | 'CANCELADO' | 'COMPLETADO') : undefined,
      desde: inputFechaDesde ? `${inputFechaDesde}T00:00:00` : undefined,
      hasta: inputFechaHasta ? `${inputFechaHasta}T23:59:59` : undefined,
    });
    setShouldFetch(true); // Activar la búsqueda
  };

  const limpiarFiltros = () => {
    setFiltros({});
    setInputCliente('');
    setInputEstado('');
    setInputFechaDesde('');
    setInputFechaHasta('');
    setShouldFetch(false); // Desactivar la búsqueda al limpiar
  };

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCopiarUrl = async () => {
    try {
      await navigator.clipboard.writeText(ecommerceUrl);
      addToast('success', 'Enlace copiado al portapapeles');
    } catch (error) {
      addToast('error', 'No se pudo copiar el enlace');
    }
  };

  const handleAbrirUrl = () => {
    window.open(ecommerceUrl, '_blank');
  };

  const handleCancelarPedido = async (pedidoId: number) => {
    await cancelarPedido(pedidoId);
    addToast('success', 'Pedido cancelado correctamente');
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#11194F] mb-2">Ecommerce</h1>
          <p className="text-gray-600">Gestión de pedidos online</p>
        </div>
        <button
          onClick={() => setShowQRModal(true)}
          disabled={!almacenId}
          className="flex items-center gap-2 px-4 py-2 bg-[#43BFFF] text-white rounded-lg hover:bg-[#2BA5E8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MdQrCode2 className="text-xl" />
          Ver QR
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow mb-6 p-6">
        <h2 className="text-lg font-bold text-[#11194F] mb-4">Filtros de Búsqueda</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-[#11194F] mb-2">Estado</label>
            <select
              value={inputEstado}
              onChange={(e) => setInputEstado(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
            >
              <option value="">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="COMPLETADO">Completado</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </div>

          {/* Nombre Cliente */}
          <div>
            <label className="block text-sm font-medium text-[#11194F] mb-2">
              Nombre Cliente
              {inputCliente.length > 0 && inputCliente.length < 3 && (
                <span className="text-xs text-orange-500 ml-2">(mín. 3 caracteres)</span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inputCliente}
                onChange={(e) => setInputCliente(e.target.value)}
                placeholder="Buscar por nombre..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && inputCliente.length >= 3) {
                    handleBuscar();
                  }
                }}
              />
            </div>
          </div>

          {/* Fecha Desde */}
          <div>
            <label className="block text-sm font-medium text-[#11194F] mb-2">Fecha Desde</label>
            <input
              type="date"
              value={inputFechaDesde}
              onChange={(e) => setInputFechaDesde(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <label className="block text-sm font-medium text-[#11194F] mb-2">Fecha Hasta</label>
            <input
              type="date"
              value={inputFechaHasta}
              onChange={(e) => setInputFechaHasta(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              min={inputFechaDesde || undefined}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleBuscar}
            className="px-6 py-2 bg-[#43BFFF] text-white rounded-lg hover:bg-[#2BA5E8] transition-colors flex items-center gap-2"
          >
            <MdSearch className="text-xl" />
            Buscar
          </button>
          <button
            onClick={limpiarFiltros}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Limpiar Filtros
          </button>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 border border-[#43BFFF] text-[#43BFFF] rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
          >
            <MdRefresh className={`text-xl ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Tabla de Pedidos */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F3F4F9]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Pedido</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Cliente</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Teléfono</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">
                  Fecha Retiro
                </th>
                <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]">Estado</th>
                <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Total</th>
                <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {!shouldFetch ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <MdSearch className="text-5xl text-gray-300" />
                      <p className="text-gray-600 font-medium">Utiliza los filtros y presiona "Buscar" para ver los pedidos</p>
                    </div>
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex justify-center">
                      <div className="w-8 h-8 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="mt-4 text-gray-600">Cargando pedidos...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-red-500">
                    Error al cargar los pedidos. Por favor, intente nuevamente.
                  </td>
                </tr>
              ) : data && data.content.length > 0 ? (
                data.content.map((pedido, index) => (
                  <tr key={pedido.pedidoId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 text-sm font-semibold text-[#11194F]">
                      #{pedido.pedidoId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{pedido.nombreCliente}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{pedido.telefonoCliente}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatearFecha(pedido.fechaHoraPickup)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          pedido.estado === 'COMPLETADO'
                            ? 'bg-green-100 text-green-700'
                            : pedido.estado === 'CANCELADO'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {pedido.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-[#11194F] text-right">
                      ${pedido.total.toLocaleString('es-CL')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <button
                          onClick={() => setPedidoSeleccionado(pedido)}
                          className="flex items-center gap-1 px-3 py-1 text-[#43BFFF] hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver detalle"
                        >
                          <MdVisibility className="text-lg" />
                          <span className="text-sm">Ver Detalle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">
                    No se encontraron pedidos con los filtros seleccionados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación Info */}
        {data && data.content.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Mostrando {data.content.length} de {data.totalElementos} pedidos
              </p>
              <p className="text-sm text-gray-600">
                Página {data.paginaActual + 1} de {data.totalPaginas}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal Detalle Pedido */}
      <ModalDetallePedido
        isOpen={!!pedidoSeleccionado}
        onClose={() => setPedidoSeleccionado(null)}
        pedido={pedidoSeleccionado}
        onCancelar={handleCancelarPedido}
      />

      {/* Modal QR */}
      {showQRModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#11194F]">QR del Ecommerce</h2>
              <button
                onClick={() => setShowQRModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="text-center">
              {/* QR Code generado dinámicamente */}
              {qrCodeUrl ? (
                <>
                  <div className="bg-white rounded-lg p-4 mb-6 border-2 border-gray-200 inline-block">
                    <img 
                      src={qrCodeUrl} 
                      alt="Código QR del Ecommerce"
                      className="w-64 h-64 mx-auto"
                    />
                  </div>
                  
                  {/* Mensaje */}
                  <p className="text-gray-700 mb-6 text-left leading-relaxed">
                    Imprime este QR y dile a tus clientes que lo escaneen para que puedan ver tu inventario, 
                    también podrán hacer pedidos a tu almacén con retiro en tienda! o cópialo y envíalo a 
                    tus clientes por Whatsapp 😉
                  </p>


                  {/* Botones de acción */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleAbrirUrl}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-[#43BFFF] text-[#43BFFF] rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <MdOpenInNew className="text-xl" />
                      Abrir URL
                    </button>
                    <button
                      onClick={handleCopiarUrl}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#43BFFF] text-white rounded-lg hover:bg-[#2BA5E8] transition-colors"
                    >
                      <MdContentCopy className="text-xl" />
                      Copiar Enlace
                    </button>
                  </div>
                </>
              ) : (
                <div className="py-12">
                  <div className="flex justify-center mb-4">
                    <div className="w-8 h-8 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
                  </div>
                  <p className="text-gray-600">Cargando información del almacén...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
